/**
 * Badge — the canonical pill component. Replaces the 4+ ad-hoc badge
 * patterns spread across CRM, NewBusiness, AIProjects, BusinessOverview,
 * Recommendations, RetailerProfiles, Merchandising.
 *
 * Two modes for the `color` prop:
 *   - Hex (#RRGGBB)      → alpha-hex string concat for bg/border
 *   - CSS var (var(--x)) → color-mix() to derive soft + border tints
 *
 * Variants are convenience shortcuts that pass the right CSS var:
 *   info / warn / danger / success / ai / muted / neutral
 *
 * Sizes:
 *   xs  — 10px font, 1px 6px padding (default — matches existing Badge)
 *   sm  — 11px font, 2px 8px padding
 */
const VARIANT_COLORS = {
  info:    'var(--info)',
  warn:    'var(--warn)',
  danger:  'var(--danger)',
  success: 'var(--success)',
  ai:      'var(--ai)',
  muted:   'var(--text-muted)',
  neutral: 'var(--text-body)',
};

const SIZES = {
  xs: { fontSize: 10, padding: '1px 6px' },
  sm: { fontSize: 11, padding: '2px 8px' },
};

export default function Badge({
  children,
  text,
  variant,
  color: colorProp,
  size = 'xs',
  bg,
  style,
}) {
  const label = text ?? children;
  if (label === undefined || label === null || label === '') return null;

  const color = colorProp || VARIANT_COLORS[variant] || VARIANT_COLORS.muted;
  const isVar = typeof color === 'string' && color.startsWith('var(');
  const computedBg = bg
    || (isVar ? `color-mix(in srgb, ${color} 14%, transparent)` : `${color}22`);
  const computedBd = isVar
    ? `color-mix(in srgb, ${color} 33%, transparent)`
    : `${color}44`;

  const s = SIZES[size] || SIZES.xs;

  return (
    <span
      style={{
        display: 'inline-block',
        fontWeight: 600,
        color,
        background: computedBg,
        border: `1px solid ${computedBd}`,
        borderRadius: 'var(--radius-sm)',
        whiteSpace: 'nowrap',
        ...s,
        ...style,
      }}
    >
      {label}
    </span>
  );
}
